import { TreeNode } from "@/types/tree";
import { findNearestTreeNodeFromHTMLElement } from "@/futurelayer/canvas/canvas-controls/util";
import { GALLERY_SLUG } from "@/lib/elements/constants";

export type HoveredGalleryImageData = {
  galleryElement: TreeNode;
  imageIndex: number;
  htmlElement: HTMLElement;
} | null;

/**
 * Detects which gallery image is currently hovered
 * @returns Gallery data with element, index, and HTML element, or null if not hovering
 */
export function findHoveredGalleryImage(target: HTMLElement, treeNode: TreeNode | null): HoveredGalleryImageData {
  if (!treeNode) return null;

  // Check if the target or any parent is a gallery item
  const galleryItem = target.closest('.ee-gallery-item');
  if (!galleryItem) return null;

  // Find the gallery element
  const gallery = galleryItem.closest('.ee-gallery');
  if (!gallery) return null;

  // Find the tree node for the gallery
  const galleryTreeNode = findNearestTreeNodeFromHTMLElement(gallery as HTMLElement);
  if (!galleryTreeNode || galleryTreeNode.data.type !== GALLERY_SLUG) {
    return null;
  }

  // Get all gallery items and find the index
  const allItems = Array.from(gallery.querySelectorAll('.ee-gallery-item'));
  const imageIndex = allItems.indexOf(galleryItem);

  if (imageIndex === -1) return null;

  return {
    galleryElement: galleryTreeNode,
    imageIndex,
    htmlElement: galleryItem as HTMLElement
  };
}

/**
 * Detects which gallery image was clicked by finding the hovered .ee-gallery-item
 * and determining its index within the gallery.
 *
 * @returns The index of the clicked gallery image, or null if not detected
 */
export function getClickedGalleryImageIndex(): number | null {
  try {
    console.log('[Gallery] Starting getClickedGalleryImageIndex');

    // Get all currently hovered elements in the canvas
    const canvasDoc = window.Breakdance.canvas.document;
    console.log('[Gallery] canvasDoc:', canvasDoc);
    if (!canvasDoc) {
      console.log('[Gallery] No canvas document found');
      return null;
    }

    // Find the clicked gallery item element by walking up from the most specific hovered element
    let currentElement = canvasDoc.querySelector('.ee-gallery-item:hover');
    console.log('[Gallery] Direct :hover query result:', currentElement);

    if (!currentElement) {
      console.log('[Gallery] Trying fallback - querying all :hover elements');
      // Fallback: try to find any hovered element and walk up to find gallery item
      const allHovered = canvasDoc.querySelectorAll(':hover');
      console.log('[Gallery] All hovered elements:', allHovered.length);

      for (let i = allHovered.length - 1; i >= 0; i--) {
        const el = allHovered[i] as HTMLElement;
        console.log('[Gallery] Checking element:', el.className);

        if (el.classList.contains('ee-gallery-item')) {
          currentElement = el;
          console.log('[Gallery] Found gallery item directly');
          break;
        }
        // Walk up to find parent gallery item
        const parent = el.closest('.ee-gallery-item');
        if (parent) {
          currentElement = parent;
          console.log('[Gallery] Found gallery item via closest()');
          break;
        }
      }
    }

    if (!currentElement) {
      console.log('[Gallery] No current element found');
      return null;
    }

    console.log('[Gallery] Found gallery item:', currentElement);

    // Get all gallery items in the same gallery
    const gallery = currentElement.closest('.ee-gallery');
    console.log('[Gallery] Parent gallery:', gallery);
    if (!gallery) {
      console.log('[Gallery] No parent gallery found');
      return null;
    }

    const allItems = Array.from(gallery.querySelectorAll('.ee-gallery-item'));
    console.log('[Gallery] All gallery items count:', allItems.length);

    const index = allItems.indexOf(currentElement);
    console.log('[Gallery] Found index:', index);

    return index >= 0 ? index : null;
  } catch (error) {
    console.error('[Gallery] Error detecting clicked gallery image:', error);
    return null;
  }
}
